import { Sigma } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceArea, ReferenceLine, CartesianGrid,
} from "recharts";

const FOOTNOTE =
  "Langelier Saturation Index (LSI) is a calculated indicator of calcium carbonate saturation tendency based on measured pH, temperature, total alkalinity, calcium hardness and total dissolved solids. It is not a directly measured laboratory analyte and should be interpreted in conjunction with the complete water analysis.";

function LsiTrend({ history }) {
  const data = history.map((p) => ({
    date: p.collected_at
      ? new Date(p.collected_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" })
      : "—",
    value: p.value,
    test: p.test_name,
  }));
  const values = data.map((d) => d.value);
  const lo = Math.min(-0.5, Math.floor(Math.min(...values) * 2) / 2 - 0.25);
  const hi = Math.max(0.5, Math.ceil(Math.max(...values) * 2) / 2 + 0.25);
  return (
    <div className="mt-5" data-testid="lsi-trend-chart">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
        LSI across repeat tests
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
          <YAxis tick={{ fontSize: 11, fill: "#64748B" }} domain={[lo, hi]} tickFormatter={(v) => v.toFixed(1)} />
          <ReferenceArea y1={0} y2={hi} fill="#F59E0B" fillOpacity={0.06} />
          <ReferenceArea y1={lo} y2={0} fill="#0EA5E9" fillOpacity={0.06} />
          <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="4 4" />
          <Tooltip
            formatter={(v) => [Number(v).toFixed(2), "LSI"]}
            labelFormatter={(l, payload) => `${l}${payload?.[0]?.payload?.test ? ` · ${payload[0].payload.test}` : ""}`}
            contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }}
          />
          <Line type="monotone" dataKey="value" stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 4, fill: "#06B6D4" }} activeDot={{ r: 6 }} />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5 text-[11px] text-slate-500">
        <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-400/40 mr-1.5 align-middle" />Above 0 — increased scale-forming tendency</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-sky-400/40 mr-1.5 align-middle" />Below 0 — increased corrosive/aggressive tendency</span>
      </div>
    </div>
  );
}

export function LsiIndexCard({ lsi, history = [], dataTestId = "lsi-index-card" }) {
  if (!lsi) return null;
  const calculated = lsi.status === "calculated";
  return (
    <div data-testid={dataTestId} className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/60">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700 flex items-center gap-1.5">
          <Sigma className="w-3.5 h-3.5" />
          Calculated Index
        </div>
        <h3 className="font-display text-lg font-bold text-slate-900 mt-1.5">
          Langelier Saturation Index (LSI)
        </h3>
      </div>
      <div className="px-6 py-5">
        {calculated ? (
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-4xl font-bold text-slate-900" data-testid="lsi-value">
              {lsi.value.toFixed(2)}
            </div>
            <span className="rounded-full bg-slate-100 text-slate-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
              Index · Calculated
            </span>
            <p className="text-sm text-slate-600 leading-relaxed w-full sm:w-auto sm:flex-1" data-testid="lsi-interpretation">
              {lsi.interpretation}
            </p>
          </div>
        ) : (
          <div className="text-sm font-semibold text-slate-500" data-testid="lsi-insufficient">
            LSI: Not calculated — insufficient analytical data
          </div>
        )}
        {history.length >= 2 && <LsiTrend history={history} />}
        <p className="mt-4 text-[11px] text-slate-400 leading-relaxed italic">{FOOTNOTE}</p>
      </div>
    </div>
  );
}
